import type {
    HTMLAttributes,
    LabelHTMLAttributes,
    ReactElement,
    ReactNode,
} from "react";
import { Heading } from "../Heading";
import { cx } from "../../utils/cx";
import styles from "./FormLayout.module.scss";

export type FormLayoutColumns = 1 | 2 | 3 | 4;

/** `full` = 100% width. `twoThirds` ≈ 66% from xl (1399px) upward. */
export type FormLayoutMaxWidth = "full" | "twoThirds";

export interface FormLayoutProps extends HTMLAttributes<HTMLDivElement> {
    children: ReactNode;
}

function FormLayoutRoot({
    children,
    className,
    ...rest
}: FormLayoutProps): ReactElement {
    return (
        <div
            className={cx(styles.root, className)}
            data-ui="form-layout"
            {...rest}
        >
            {children}
        </div>
    );
}
FormLayoutRoot.displayName = "FormLayout";

export interface FormLayoutSectionProps
    extends Omit<HTMLAttributes<HTMLElement>, "title"> {
    /** Section heading (DS `Heading` level 6 — 18px form section titles). */
    title?: ReactNode;
    children: ReactNode;
}

function Section({
    title,
    children,
    className,
    ...rest
}: FormLayoutSectionProps): ReactElement {
    return (
        <section className={cx(styles.section, className)} {...rest}>
            {title != null ? (
                <Heading className={styles.sectionTitle} level={6}>
                    {title}
                </Heading>
            ) : null}
            {children}
        </section>
    );
}
Section.displayName = "FormLayout.Section";

export interface FormLayoutGridProps extends HTMLAttributes<HTMLDivElement> {
    /**
     * Desktop column count.
     * - `2`: 1 col → 2 from 768px
     * - `3`: 1 → 2 (768) → 3 (1200) — Generals-style
     * - `4`: 1 → 2 (768) → 4 (1200) — Customer details-style
     * - `1`: always single column
     */
    columns?: FormLayoutColumns;
    maxWidth?: FormLayoutMaxWidth;
    children: ReactNode;
}

function Grid({
    columns = 3,
    maxWidth = "full",
    children,
    className,
    ...rest
}: FormLayoutGridProps): ReactElement {
    return (
        <div
            className={cx(styles.grid, className)}
            data-columns={columns}
            data-max-width={maxWidth}
            data-ui="form-layout-grid"
            {...rest}
        >
            {children}
        </div>
    );
}
Grid.displayName = "FormLayout.Grid";

export interface FormLayoutFullProps extends HTMLAttributes<HTMLDivElement> {
    children: ReactNode;
}

/** Spans all columns inside a `FormLayout.Grid`. */
function Full({
    children,
    className,
    ...rest
}: FormLayoutFullProps): ReactElement {
    return (
        <div className={cx(styles.full, className)} {...rest}>
            {children}
        </div>
    );
}
Full.displayName = "FormLayout.Full";

export interface FormLayoutFieldProps extends HTMLAttributes<HTMLDivElement> {
    /** Optional label above the control (e.g. Switch without DS floating label). */
    label?: ReactNode;
    htmlFor?: LabelHTMLAttributes<HTMLLabelElement>["htmlFor"];
    children: ReactNode;
}

function Field({
    label,
    htmlFor,
    children,
    className,
    ...rest
}: FormLayoutFieldProps): ReactElement {
    return (
        <div className={cx(styles.field, className)} {...rest}>
            {label != null ? (
                <label className={styles.fieldLabel} htmlFor={htmlFor}>
                    {label}
                </label>
            ) : null}
            {children}
        </div>
    );
}
Field.displayName = "FormLayout.Field";

export const FormLayout = Object.assign(FormLayoutRoot, {
    Section,
    Grid,
    Full,
    Field,
});
